import React, { useCallback, useEffect, useState } from 'react';
import {
  listSharedQuizzes,
  bulkDeleteSharedQuizzes,
  SharedQuizAdminListItem,
} from '../../Game/api/gameApi';
import CustomQuizViewModal from './CustomQuizViewModal';
import CustomQuizCreateModal from './CustomQuizCreateModal';

const CustomQuizManagementTab: React.FC = () => {
  const [quizzes, setQuizzes] = useState<SharedQuizAdminListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewingQuizId, setViewingQuizId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listSharedQuizzes();
      setQuizzes(res);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load custom quizzes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === quizzes.length ? new Set() : new Set(quizzes.map((q) => q.id)),
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} custom game(s)? This action cannot be undone.`)) {
      return;
    }
    try {
      setIsDeleting(true);
      await bulkDeleteSharedQuizzes(Array.from(selectedIds));
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete custom games.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Custom Games</h2>
          <p className="text-sm text-gray-600">{quizzes.length} total custom games</p>
        </div>
        <div className="flex items-center space-x-3">
          {selectedIds.size > 0 && (
            <span className="text-sm text-gray-600">{selectedIds.size} selected</span>
          )}
          <button
            onClick={handleDeleteSelected}
            disabled={selectedIds.size === 0 || isDeleting}
            className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            {isDeleting ? 'Deleting...' : `Delete Selected${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
          </button>
          <button
            onClick={loadData}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            + Add Custom Game
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={quizzes.length > 0 && selectedIds.size === quizzes.length}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 text-blue-600 rounded"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Join Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Creator</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">Loading custom games...</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-red-600">{error}</td>
              </tr>
            ) : quizzes.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">No custom games found</td>
              </tr>
            ) : (
              quizzes.map((q) => (
                <tr key={q.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(q.id)}
                      onChange={() => toggleSelect(q.id)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{q.title || 'Untitled'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 font-mono">{q.join_code}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      q.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {q.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{q.created_by_username || 'N/A'}</td>
                  <td className="px-6 py-4 text-sm">
                    <button
                      onClick={() => setViewingQuizId(q.id)}
                      className="text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {viewingQuizId !== null && (
        <CustomQuizViewModal
          quizId={viewingQuizId}
          onClose={() => setViewingQuizId(null)}
          onSaved={() => loadData()}
        />
      )}
      {showCreateModal && (
        <CustomQuizCreateModal
          onClose={() => setShowCreateModal(false)}
          onSaved={() => loadData()}
        />
      )}
    </div>
  );
};

export default CustomQuizManagementTab;